import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { ANALYST_LABEL } from '../lib/agents.js'
import KitchenLoader from './KitchenLoader.jsx'

// AskAiModal — the AI chat surface opened from the sticky AskAiBar.
//
// TWO modes, selected by the `mode` prop:
//
//   • default (coming-soon stub) — every tab EXCEPT Insights. Read-only,
//     no input, no submit, no model call. We ship the SHAPE of the
//     AI-forward product so customers see "AI is here" without exposing
//     a chat surface we're still polishing. Behavior is unchanged from
//     the Phase 1 stub.
//
//   • 'insights-query' — the Insights tab only (Ship I). A real text
//     input + submit that calls the ask-restaurant edge function and
//     renders a one-line answer, a simple rows table, and the agent
//     label. KitchenLoader (steaming pot) while in flight — never the
//     blue spinner.
//
// Props:
//   open, onClose — visibility control.
//   placeholder   — the bar's copy, echoed as an eyebrow.
//   mode          — undefined → coming-soon stub. 'insights-query' →
//                   real query mode.
//   restaurantId  — multi-unit scope. Pass currentRestaurantId
//                   (null for single-unit; the edge fn falls back to the
//                   caller's first membership). Sent as restaurant_id.
//
// CLAUDE.md React-hook-ordering: ALL hooks are declared BEFORE any
// conditional return — this is the exact bug that bit PromotionsScreen.
//
// CLAUDE.md submit-state rule: setSubmitting(false) lives in `finally`,
// not `catch`, so the form never locks on the next open.
export default function AskAiModal({ open, onClose, placeholder, mode, restaurantId }) {
  const confirmButtonRef = useRef(null)
  const inputRef = useRef(null)

  const isQueryMode = mode === 'insights-query'

  // Query-mode state (declared unconditionally — see hook-ordering note).
  const [question, setQuestion] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)   // { answer, rows, view_label, agent_label }
  const [errorMsg, setErrorMsg] = useState(null)

  // Open-side a11y: Escape-to-close + initial focus. In query mode we
  // focus the input; in stub mode we focus the primary CTA.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    if (isQueryMode) {
      inputRef.current?.focus()
    } else {
      confirmButtonRef.current?.focus()
    }
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose, isQueryMode])

  // Reset query-mode state whenever the modal closes so a re-open starts
  // clean (the modal stays mounted between opens).
  useEffect(() => {
    if (!open) {
      setQuestion('')
      setSubmitting(false)
      setResult(null)
      setErrorMsg(null)
    }
  }, [open])

  async function handleSubmit(e) {
    e?.preventDefault?.()
    const trimmed = question.trim()
    if (submitting || trimmed.length < 3) return
    setSubmitting(true)
    setErrorMsg(null)
    setResult(null)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const payload = { question: trimmed }
      // Multi-unit convention: only send restaurant_id when we have a
      // concrete unit (single-unit / roll-up pass null → omitted).
      if (restaurantId) payload.restaurant_id = restaurantId

      const resp = await fetch(`${supabaseUrl}/functions/v1/ask-restaurant`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify(payload),
      })

      // Rate-limit / service-unavailable / non-200 → deterministic copy.
      if (!resp.ok) {
        // eslint-disable-next-line no-console
        console.error('[AskAiModal] ask-restaurant non-200', resp.status)
        if (resp.status === 429) {
          setErrorMsg('Give it a few seconds before asking again.')
        } else {
          setErrorMsg("I couldn't analyze that just now. Try again in a moment.")
        }
        return
      }

      const data = await resp.json()
      if (data?.error) {
        // eslint-disable-next-line no-console
        console.error('[AskAiModal] ask-restaurant error payload', data)
        setErrorMsg("I couldn't analyze that just now. Try again in a moment.")
        return
      }
      setResult({
        answer: data?.answer || '',
        rows: Array.isArray(data?.rows) ? data.rows : [],
        view_label: data?.view_label || null,
        agent_label: data?.agent_label || ANALYST_LABEL,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AskAiModal] submit failed', err)
      setErrorMsg("I couldn't analyze that just now. Try again in a moment.")
    } finally {
      // CLAUDE.md: reset submit-state in finally, NEVER only in catch.
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Scrim — tap-to-dismiss. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      {/* Sheet — slides up from the bottom on mobile. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Ask AI"
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
      >
        {/* Drag handle (visual only). */}
        <div className="pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
        </div>

        {isQueryMode
          ? renderQueryMode()
          : renderComingSoon()}
      </div>
    </div>
  )

  // ----- Coming-soon stub (unchanged Phase 1 behavior) -----------------
  function renderComingSoon() {
    return (
      <>
        <div className="px-5 pt-3 pb-4">
          {placeholder ? (
            <div className="text-[11px] font-medium text-charcoal-text-secondary">
              {placeholder}
            </div>
          ) : null}

          <h2 className="mt-1 text-xl font-medium text-charcoal-hero">
            AI chat is coming soon
          </h2>

          <p className="mt-3 text-sm leading-relaxed text-black/75">
            You'll be able to ask questions about your kitchen here —
            your inventory, your sales, your schedule, the lot. We're
            polishing it now and want to ship it without rough edges.
          </p>

          <p className="mt-3 text-sm leading-relaxed text-black/75">
            In the meantime, every tab already has the AI working in
            the background — drafting your social posts, scanning your
            invoices, watching your waste patterns.
          </p>
        </div>

        <div className="sticky bottom-0 border-t border-black/5 bg-white px-5 pt-3 safe-bottom">
          <button
            type="button"
            onClick={onClose}
            ref={confirmButtonRef}
            className="min-h-btn w-full rounded-lg bg-amber-brand-800 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-amber-brand-600 active:bg-amber-brand-900"
          >
            Got it
          </button>
        </div>
      </>
    )
  }

  // ----- Real query mode (Insights tab only) ---------------------------
  function renderQueryMode() {
    return (
      <>
        <div className="px-5 pt-3 pb-4">
          {placeholder ? (
            <div className="text-[11px] font-medium text-charcoal-text-secondary">
              {placeholder}
            </div>
          ) : null}

          <h2 className="mt-1 text-xl font-medium text-charcoal-hero">
            Ask your restaurant
          </h2>

          <p className="mt-2 text-sm leading-relaxed text-black/70">
            Ask a question about your menu, margins, or compliance
            documents and the analyst will read your data and answer.
          </p>

          <form onSubmit={handleSubmit} className="mt-4">
            <label htmlFor="ask-ai-question" className="sr-only">
              Your question
            </label>
            <textarea
              id="ask-ai-question"
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={2}
              maxLength={500}
              disabled={submitting}
              placeholder="Which menu items make the most money?"
              className="w-full resize-none rounded-lg border border-black/15 px-3 py-2 text-base text-black/90 shadow-sm focus:border-amber-brand-600 focus:outline-none focus:ring-1 focus:ring-amber-brand-600 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={submitting || question.trim().length < 3}
              className="mt-3 min-h-btn w-full rounded-lg bg-amber-brand-800 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-amber-brand-600 active:bg-amber-brand-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Ask
            </button>
          </form>

          {/* Loading — KitchenLoader (steaming pot), never the blue spinner. */}
          {submitting && (
            <div className="mt-5">
              <KitchenLoader
                align="row"
                size="sm"
                messages={[
                  'Reading your data…',
                  'Crunching the numbers…',
                  'Writing your answer…',
                ]}
              />
            </div>
          )}

          {/* Deterministic error / nudge copy. */}
          {!submitting && errorMsg && (
            <div className="mt-5 rounded-lg bg-black/5 px-3 py-3 text-sm leading-relaxed text-black/75">
              {errorMsg}
            </div>
          )}

          {/* Answer + rows table + agent label. */}
          {!submitting && result && (
            <div className="mt-5">
              <div className="text-sm leading-relaxed text-black/85">
                {result.answer}
              </div>

              {result.view_label && (
                <div className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-black/45">
                  {result.view_label}
                </div>
              )}

              {result.rows.length > 0 && (
                <div className="mt-2 overflow-x-auto rounded-lg border border-black/10">
                  <RowsTable rows={result.rows} />
                </div>
              )}

              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-black/50">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-brand-600" />
                <span>{result.agent_label || ANALYST_LABEL}</span>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-black/5 bg-white px-5 pt-3 safe-bottom">
          <button
            type="button"
            onClick={onClose}
            className="min-h-btn w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base font-medium text-black/80 shadow-sm hover:bg-black/5"
          >
            Close
          </button>
        </div>
      </>
    )
  }
}

// Simple, defensive rows table. Columns are derived from the first row's
// keys (the edge fn returns a fixed projection per view), internal keys
// are dropped, and cell values are rendered as plain strings.
function RowsTable({ rows }) {
  const first = rows[0] || {}
  const columns = Object.keys(first).filter((k) => k !== 'item_id' && k !== 'id')
  if (columns.length === 0) return null

  return (
    <table className="w-full border-collapse text-left text-xs">
      <thead>
        <tr className="bg-black/[0.03]">
          {columns.map((col) => (
            <th
              key={col}
              className="whitespace-nowrap px-2.5 py-2 font-semibold text-black/55"
            >
              {humanizeColumn(col)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-t border-black/5">
            {columns.map((col) => (
              <td key={col} className="whitespace-nowrap px-2.5 py-2 tabular-nums text-black/80">
                {formatCell(row[col])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function humanizeColumn(col) {
  return String(col).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatCell(val) {
  if (val == null) return '—'
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  return String(val)
}
